import { isClientSide } from "@helper/utils";
import { useEffect, useRef, useState } from "react";

export default function useVisibleArticle(triggerState: string) {
  const [visibleArticleId, setVisibleArticleId] = useState<
    string | undefined
  >();
  const visibleEntriesRef = useRef<Map<Element, number>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!isClientSide) return;

    let rafId = 0;

    const findMostVisibleArticle = () => {
      let maxVisibleId = "";
      let maxVisibility = 0;

      visibleEntriesRef.current.forEach((visibility, element) => {
        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          maxVisibleId = element.id;
        }
      });

      setVisibleArticleId(maxVisibleId);
    };

    const findArticleAtViewportMidpoint = () => {
      const articles = Array.from(
        document.querySelectorAll<HTMLElement>(".article-card"),
      );
      if (articles.length === 0) return;

      const viewportMidpoint = window.innerHeight / 2;
      const activeArticle = articles.find((article) => {
        const rect = article.getBoundingClientRect();
        return rect.top <= viewportMidpoint && rect.bottom >= viewportMidpoint;
      });

      if (activeArticle?.id) {
        setVisibleArticleId(activeArticle.id);
      }
    };

    const scheduleViewportMidpointCheck = () => {
      if (rafId) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        findArticleAtViewportMidpoint();
      });
    };

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleEntriesRef.current.set(
              entry.target,
              entry.intersectionRatio,
            );
          } else {
            visibleEntriesRef.current.delete(entry.target);
          }
        });
        findMostVisibleArticle();
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: [0.1, 0.25, 0.5, 0.75, 1.0], // Explicitly float for clarity
      },
    );

    const articles = document.querySelectorAll(".article-card");
    articles.forEach((article) => observerRef.current?.observe(article));
    findArticleAtViewportMidpoint();
    window.addEventListener("scroll", scheduleViewportMidpointCheck, {
      passive: true,
    });
    window.addEventListener("resize", scheduleViewportMidpointCheck);

    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener("scroll", scheduleViewportMidpointCheck);
      window.removeEventListener("resize", scheduleViewportMidpointCheck);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [triggerState]); // Re-run effect only when triggerState changes

  return visibleArticleId;
}
